import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Settings, LogOut, ChevronDown, AlertTriangle, Package, Receipt, X, Printer, BarChart3, Folder, Scale, RefreshCw, Truck, ShoppingBag, ArrowUpDown } from 'lucide-react';
import { useAuthStore } from '../../lib/auth';
import { useSettingsStore, useCartStore, useOrderStore, useProductStore, useSync } from '../../hooks';
import { useToast, Button } from '@lancar-pos/ui';
import { Menu, MenuTrigger, MenuPositioner, MenuContent, MenuItem, MenuSeparator } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import { ProductGrid } from './ProductGrid';
import { Cart } from './Cart';
import { CartDrawer } from './CartDrawer';
import { PaymentScreen } from './PaymentScreen';
import { SyncIndicator } from './SyncIndicator';
import { useDatabase } from '../../db/DatabaseProvider';
import { ReceiptContent, usePrintReceipt } from '../receipt/ReceiptPrint';
import { usePwaUpdate } from '../../hooks/usePwaUpdate';
import type { Order } from '../../hooks/useOrders';

export function POSTerminal() {
  const { user, signOut } = useAuthStore();
  const { getSetting } = useSettingsStore();
  const { items, clearCart, getSubtotal, getTax, getTotal } = useCartStore();
  const { products } = useProductStore();
  const { addOrder } = useOrderStore();
  const { isSyncing, isOnline, error, syncAll, isConfigured } = useSync();
  const { showUpdateBanner, latestVersion, installUpdate, dismissBanner } = usePwaUpdate();
  const navigate = useNavigate();
  const toast = useToast();
  const db = useDatabase();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [lastOrder, setLastOrder] = useState<Order | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { handlePrint } = usePrintReceipt();

  const businessName = getSetting<string>('business_name') || 'LancarPOS';
  const taxRate = getSetting<number>('tax_rate') ?? 11;
  const taxEnabled = getSetting<boolean>('tax_enabled') !== false;

  const handleCheckout = () => {
    if (items.length === 0) {
      toast.error('Keranjang kosong');
      return;
    }
    setShowPaymentScreen(true);
  };

  const handleProcessPayment = async (paidAmount: number) => {
    if (items.length === 0) return;

    setIsProcessing(true);
    setShowPaymentScreen(false);

    try {
      const subtotal = getSubtotal();
      const tax = taxEnabled ? getTax(taxRate) : 0;
      const total = getTotal(taxRate, taxEnabled);
      const changeAmount = Math.max(0, paidAmount - total);

      // Validate stock availability before creating order
      for (const item of items) {
        const actualProductId = item.variantId ? item.productId.split(':')[0] : item.productId;
        const product = products.find((p) => p.id === actualProductId);
        
        if (item.variantId && product?.variants) {
          // Validate variant stock
          const variant = product.variants.find((v) => v.id === item.variantId);
          if (variant && variant.stock < item.quantity) {
            toast.error(
              `Stok tidak cukup untuk ${product.name} (${item.variantName})`,
              `Tersedia: ${variant.stock}, Dibutuhkan: ${item.quantity}`
            );
            return;
          }
        } else if (product && !item.variantId && product.stock < item.quantity) {
          // Validate simple product stock
          toast.error(
            `Stok tidak cukup untuk ${product.name}`,
            `Tersedia: ${product.stock}, Dibutuhkan: ${item.quantity}`
          );
          return;
        }
      }

      const newOrder = await addOrder({
        items: items.map((item) => ({
          id: crypto.randomUUID(),
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          price: item.price,
          discount: 0,
          subtotal: item.price * item.quantity,
          variant: item.variantId ? { id: item.variantId, name: item.variantName || '', priceModifier: 0 } : null,
          notes: item.notes,
        })),
        subtotal,
        discount: 0,
        discountType: 'amount',
        tax,
        total,
        paymentMethod: 'cash',
        paidAmount: paidAmount,
        changeAmount: changeAmount,
        status: 'completed',
        customerId: null,
        tableNumber: null,
        orderType: 'takeaway',
        kitchenNotes: '',
        receiptPrinted: false,
        syncedAt: null,
      } as Parameters<typeof addOrder>[0]);

      // Decrease stock after order is successfully created
      for (const item of items) {
        const actualProductId = item.variantId ? item.productId.split(':')[0] : item.productId;
        const product = products.find((p) => p.id === actualProductId);
        
        if (item.variantId && product?.variants) {
          // Decrease variant stock
          const updatedVariants = product.variants.map((v) => {
            if (v.id === item.variantId) {
              return { ...v, stock: Math.max(0, v.stock - item.quantity) };
            }
            return v;
          });
          const doc = await db.products.findOne(actualProductId).exec();
          if (doc) {
            await doc.patch({
              variants: updatedVariants,
              updatedAt: new Date().toISOString(),
            });
          }
        } else if (product && !item.variantId) {
          // Decrease simple product stock
          const doc = await db.products.findOne(actualProductId).exec();
          if (doc) {
            await doc.patch({
              stock: Math.max(0, doc.stock - item.quantity),
              updatedAt: new Date().toISOString(),
            });
          }
        }
      }

      clearCart();
      
      const autoPrintEnabled = getSetting<boolean>('auto_print_enabled') !== false;
      if (autoPrintEnabled && newOrder) {
        setLastOrder(newOrder);
        setShowReceiptModal(true);
      }
      toast.success('Pesanan berhasil!', `Total: ${formatCurrency(total)}`);
    } catch (error) {
      toast.error('Gagal memproses pesanan');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    try {
      await db.products.find().remove();
      await db.categories.find().remove();
      await db.orders.find().remove();
      await db.customers.find().remove();
      await db.settings.find().remove();
      await db.sync_meta.find().remove();
      clearCart();
      await signOut();
      navigate({ to: '/app/login' });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Gagal keluar');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutDialog(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {showUpdateBanner && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            <span className="text-sm font-medium">
              Pembaruan tersedia: v{latestVersion}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={installUpdate}
              className="px-3 py-1 bg-white text-amber-600 text-sm font-medium rounded-lg hover:bg-amber-50 transition-colors"
            >
              Perbarui Sekarang
            </button>
            <button
              onClick={dismissBanner}
              className="p-1 hover:bg-amber-400 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary-600">{businessName}</h1>

          <div className="flex items-center gap-3">
            <SyncIndicator
              isOnline={isOnline}
              isSyncing={isSyncing}
              error={isConfigured ? error : null}
              onRetry={syncAll}
            />

            <Menu>
              <MenuTrigger asChild>
                <button className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-medium text-sm">
                      {user?.full_name?.charAt(0) || user?.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-gray-900 truncate max-w-[120px]">
                    {user?.full_name || user?.email}
                  </span>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
              </MenuTrigger>
              <MenuPositioner>
                <MenuContent>
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/orders' })}>
                    <Receipt className="w-4 h-4" />
                    Riwayat Pesanan
                  </MenuItem>
                  <MenuItem onClick={() => navigate({ to: '/app/dashboard' })}>
                    <BarChart3 className="w-4 h-4" />
                    Dashboard
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/products' })}>
                    <Package className="w-4 h-4" />
                    Produk
                  </MenuItem>
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/categories' })}>
                    <Folder className="w-4 h-4" />
                    Kategori
                  </MenuItem>
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/units' })}>
                    <Scale className="w-4 h-4" />
                    Satuan
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/suppliers' })}>
                    <Truck className="w-4 h-4" />
                    Supplier
                  </MenuItem>
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/purchase-orders' })}>
                    <ShoppingBag className="w-4 h-4" />
                    Pembelian
                  </MenuItem>
                  <MenuItem onClick={() => navigate({ to: '/app/inventory/stock-adjustments' })}>
                    <ArrowUpDown className="w-4 h-4" />
                    Penyesuaian Stok
                  </MenuItem>
                  <MenuSeparator />
                  <MenuItem onClick={() => navigate({ to: '/app/settings' })}>
                    <Settings className="w-4 h-4" />
                    Pengaturan
                  </MenuItem>
                  <MenuItem onClick={() => setShowLogoutDialog(true)}>
                    <LogOut className="w-4 h-4" />
                    Keluar
                  </MenuItem>
                </MenuContent>
              </MenuPositioner>
            </Menu>
          </div>
        </div>
      </header>

      <main className="flex flex-col lg:flex-row h-[calc(100vh-57px)]">
        <div className="flex-1 p-4 lg:p-6 overflow-hidden pb-24 lg:pb-6">
          <ProductGrid />
        </div>

        <div className="hidden lg:block w-80 xl:w-96 p-4 lg:p-6 lg:pl-0">
          <Cart onCheckout={handleCheckout} />
        </div>
      </main>

      <div className="lg:hidden">
        <CartDrawer onCheckout={handleCheckout} />
      </div>

      {showLogoutDialog && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowLogoutDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Konfirmasi Keluar</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Semua data lokal akan dihapus setelah keluar. Data yang sudah disinkronkan ke Google Sheets tetap aman. Lanjutkan?
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowLogoutDialog(false)} disabled={isLoggingOut}>
                Batal
              </Button>
              <Button variant="danger" onClick={handleSignOut} loading={isLoggingOut}>
                Keluar
              </Button>
            </div>
          </div>
        </>
      )}

      {showReceiptModal && lastOrder && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowReceiptModal(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                <h2 className="text-lg font-bold">Struk Pesanan</h2>
                <button
                  onClick={() => setShowReceiptModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ReceiptContent
                order={lastOrder}
                businessName={businessName}
                businessAddress={getSetting<string>('business_address') || ''}
                businessPhone={getSetting<string>('business_phone') || ''}
                width={getSetting<number>('receipt_width') || 58}
                receiptFooter={getSetting<string>('receipt_footer') || ''}
              />
              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowReceiptModal(false)}>
                  Tutup
                </Button>
                <Button className="flex-1" onClick={() => handlePrint()}>
                  <Printer className="w-4 h-4 mr-2" />
                  Cetak
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {showPaymentScreen && (
        <PaymentScreen
          onPay={handleProcessPayment}
          onCancel={() => setShowPaymentScreen(false)}
          loading={isProcessing}
        />
      )}
    </div>
  );
}
